<script setup>
import { computed, ref, useSlots } from 'vue';

import HrIconBtn from './HrIconBtn.vue';

/**
 * Dismissible alert banner with icon, heading, and optional action area.
 *
 * @status internal
 * @group status-feedback
 * @see https://quasar.dev/vue-components/banner Quasar q-banner API
 */
defineOptions({ name: 'HrBanner' });

const props = defineProps({
  /**
   * Color role controlling the banner's surface, icon, and border colors.
   * @values primary, success, warning, error
   */
  colorRole: {
    type: String,
    default: 'primary',
    validator: (v) => ['primary', 'success', 'warning', 'error'].includes(v),
  },
  /** Material icon name shown in the leading icon container. */
  icon: {
    type: String,
    default: null,
  },
  /** Accessible label for the icon — set when the icon conveys meaning rather than being decorative. */
  iconLabel: {
    type: String,
    default: null,
  },
  /**
   * Banner body text. Rendered in the default slot area when no slot content is provided.
   * @replacesSlot default
   */
  message: {
    type: String,
    default: null,
  },
  /** Optional heading rendered above the message text. */
  heading: {
    type: String,
    default: null,
  },
  /**
   * HTML heading level for the heading element.
   * @values h1, h2, h3, h4, h5, h6
   * @deprecated Banners should be uniform in heading level. This prop will be removed in a future release.
   */
  headingLevel: {
    type: String,
    default: 'h2',
    validator: (v) => ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(v),
  },
  /** When true, removes the border radius so the banner spans edge-to-edge in its container. */
  fullBleed: {
    type: Boolean,
    default: false,
  },
  /** When true, stacks the action area below the content instead of inline. */
  multiLine: {
    type: Boolean,
    default: false,
  },
  /** When true, shows a dismiss (×) button that hides the banner on click. */
  dismissible: {
    type: Boolean,
    default: false,
  },
  /** Accessible label for the dismiss button. */
  closeLabel: {
    type: String,
    default: 'Dismiss banner',
  },
  /** When true, uses role="alert" and aria-live="assertive" to interrupt screen readers. */
  assertive: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** Emitted when the user clicks the dismiss button; the banner hides itself. */
  dismiss: null,
});
const slots = useSlots();

const isVisible = ref(true);
const hasIcon = computed(() => !!slots.icon || props.icon !== null);
const hasAside = computed(() => !!slots.action || props.dismissible);

function dismiss() {
  isVisible.value = false;
  emit('dismiss');
}

function show() {
  isVisible.value = true;
}

defineExpose({ show, dismiss });
</script>

<template>
  <div
    v-if="isVisible"
    :role="assertive ? 'alert' : 'status'"
    :aria-live="assertive ? 'assertive' : 'polite'"
    :class="[
      'hr-banner',
      `hr-banner--${colorRole}`,
      fullBleed && 'hr-banner--full-bleed',
      multiLine && 'hr-banner--multi-line',
      hasAside && !multiLine && 'hr-banner--has-aside',
    ]"
  >
    <div class="hr-banner__main">
      <div
        v-if="hasIcon"
        class="hr-banner__icon"
      >
        <!-- @slot icon Custom icon element; defaults to a q-icon using the `icon` prop when not provided. -->
        <slot name="icon">
          <q-icon
            :name="icon"
            size="sm"
            :role="iconLabel ? 'img' : undefined"
            :aria-label="iconLabel ?? undefined"
            :aria-hidden="!iconLabel"
          />
        </slot>
      </div>

      <div class="hr-banner__content">
        <component
          :is="headingLevel"
          v-if="heading"
          class="hr-banner__heading"
        >
          {{ heading }}
        </component>
        <div class="hr-banner__message">
          <!-- @slot default Banner body content; falls back to the `message` prop when not provided. -->
          <slot>{{ message }}</slot>
        </div>
      </div>
    </div>

    <div
      v-if="hasAside"
      class="hr-banner__aside"
    >
      <div
        v-if="$slots.action"
        class="hr-banner__action"
      >
        <!-- @slot action Action buttons (e.g. HrBtn) displayed to the right of the content. -->
        <slot name="action" />
      </div>
      <hr-icon-btn
        v-if="dismissible"
        icon="close"
        size="sm"
        :aria-label="closeLabel"
        :legacy-props="{ flat: true, round: true, dense: true }"
        @click="dismiss"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
// ── Color role locals ─────────────────────────────────────────
// CSS custom property chains fall back to SCSS $vars when no theme is set
// (--app-* resolves to guaranteed-invalid because --md-sys-color-* is undefined).

.hr-banner {
  --_bg:         var(--app-primary-container,    #{$info-bg-light});
  --_fg:         var(--app-on-primary-container, inherit);
  --_icon-bg:    var(--app-primary,              #{$info-bg});
  --_icon-fg:    var(--app-on-primary,           white);
  --_btn-color:  var(--app-on-primary-container, #{$info-bg});
  --_btn-border: var(--app-on-primary-container, #{$info-bg});
  // The close button is a standard icon button, which otherwise takes primary rather than the role.
  --_icon-btn-standard-color: var(--_btn-color);
}

.hr-banner--primary {
  --_bg:         var(--app-primary-container,    #{$info-bg-light});
  --_fg:         var(--app-on-primary-container, inherit);
  --_icon-bg:    var(--app-primary,              #{$info-bg});
  --_icon-fg:    var(--app-on-primary,           white);
  --_btn-color:  var(--app-on-primary-container, #{$info-bg});
  --_btn-border: var(--app-on-primary-container, #{$info-bg});
}

.hr-banner--success {
  --_bg:         var(--app-success-container,    #{$success-bg-light});
  --_fg:         var(--app-on-success-container, inherit);
  --_icon-bg:    var(--app-success,              #{$success-bg});
  --_icon-fg:    var(--app-on-success,           white);
  --_btn-color:  var(--app-on-success-container, #{$success-bg});
  --_btn-border: var(--app-on-success-container, #{$success-bg});
}

.hr-banner--warning {
  --_bg:         var(--app-warning-container,    #{$warning-bg-light});
  --_fg:         var(--app-on-warning-container, inherit);
  --_icon-bg:    var(--app-warning,              #{$warning-bg});
  --_icon-fg:    var(--app-on-warning,           white);
  --_btn-color:  var(--app-on-warning-container, #{$warning-bg});
  --_btn-border: var(--app-on-warning-container, #{$warning-bg});
}

.hr-banner--error {
  --_bg:         var(--app-error-container,      #{$negative-bg-light});
  --_fg:         var(--app-on-error-container,   inherit);
  --_icon-bg:    var(--app-error,                #{$negative-bg});
  --_icon-fg:    var(--app-on-error,             white);
  --_btn-color:  var(--app-on-error-container,   #{$negative-bg});
  --_btn-border: var(--app-on-error-container,   #{$negative-bg});
}

// ── Layout ────────────────────────────────────────────────────

.hr-banner {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 16px 24px 16px 16px;
  box-sizing: border-box;
  border-radius: var(--app-shape-corner-medium, 12px);
  background: var(--_bg);
  color: var(--_fg);
}

.hr-banner__main {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
}

.hr-banner__icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: var(--app-shape-corner-extra-small, 4px);
  background: var(--_icon-bg);
  color: var(--_icon-fg);
  display: flex;
  align-items: center;
  justify-content: center;
}

.hr-banner__content {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px;
}

.hr-banner__heading {
  margin: 0;
  font-size:      var(--app-type-label-large-size,        0.875rem);
  font-weight:    var(--app-type-label-large-weight,      500);
  line-height:    var(--app-type-label-large-line-height, 1.25rem);
  letter-spacing: var(--app-type-label-large-tracking,    0.00625em);
}

.hr-banner__message {
  font-size:      var(--app-type-body-medium-size,        0.875rem);
  font-weight:    var(--app-type-body-medium-weight,      400);
  line-height:    var(--app-type-body-medium-line-height, 1.25rem);
  letter-spacing: var(--app-type-body-medium-tracking,    0.015625em);
}

.hr-banner__aside {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  align-items: center;
}

.hr-banner__action {
  display: flex;
  gap: 8px;
  align-items: center;

  @media only screen and (max-width: $breakpoint-xs) {
    flex-wrap: wrap;
    justify-content: flex-end;
  }
}

// ── Single-line responsive collapse ───────────────────────────

.hr-banner--has-aside {
  @media only screen and (max-width: $breakpoint-sm) {
    flex-direction: column;
    align-items: stretch;
  }

  .hr-banner__main {
    @media (max-width: $breakpoint-sm) {
      width: 100%;
      align-items: flex-start;
    }
  }

  .hr-banner__aside {
    @media only screen and (max-width: $breakpoint-sm) {
      width: 100%;
      justify-content: flex-end;
    }
  }
}

// ── Full-bleed ────────────────────────────────────────────────

.hr-banner--full-bleed {
  border-radius: 0;
}

// ── Multi-line variant ────────────────────────────────────────

.hr-banner--multi-line {
  padding: 24px;
  flex-wrap: wrap;
  align-items: flex-start;

  /*
   * Both rows take the full line so the aside wraps beneath the message instead of sitting
   * alongside it. Without this the wrap never triggers: `__main` is `flex: 1` (basis 0) and
   * `__aside` is `flex-shrink: 0`, so main collapses to min-content — one word per line — and the
   * action keeps its place on the right. That is the whole point of the multi-line variant, and
   * the `justify-content: flex-end` below only does anything once the aside spans the row.
   */
  .hr-banner__main {
    align-items: flex-start;
    flex-basis: 100%;
  }

  .hr-banner__content {
    display: block;
  }

  .hr-banner__heading {
    display: block;
    margin-bottom: 4px;
  }

  .hr-banner__message {
    display: block;
  }

  .hr-banner__aside {
    flex-basis: 100%;
    justify-content: flex-end;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hr-banner {
    transition: none !important;
    animation: none !important;
  }
}
</style>
